import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatPence,
  formatPenceSigned,
  type TicketView,
  type TakingsView,
  type TicketCounts,
  type TicketStatus,
  type TicketSearchHit,
} from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import type { StaffSession } from '../lib/session.js';
import { Button, Card, Banner } from '../components/ui.js';

interface StaffEvent {
  id: string;
  name: string;
  entryFeePence: number;
  isFree: boolean;
  isPractice: boolean;
  joinCode: string | null;
}

/** Plain words, because "PENDING" means nothing to somebody working a door. */
const FILTERS: { value: TicketStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Unsold' },
  { value: 'PAID', label: 'Sold, unused' },
  { value: 'REDEEMED', label: 'Used' },
  { value: 'REFUNDED', label: 'Refunded' },
];

export function DoorTab({ staff, event }: { staff: StaffSession; event: StaffEvent }) {
  const [tickets, setTickets] = useState<TicketView[]>([]);
  const [takings, setTakings] = useState<TakingsView | null>(null);
  const [counts, setCounts] = useState<TicketCounts | null>(null);
  const [batch, setBatch] = useState(20);
  const [refunding, setRefunding] = useState<TicketView | null>(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<TicketSearchHit[] | null>(null);
  const [justGenerated, setJustGenerated] = useState<TicketView[] | null>(null);
  const [filter, setFilter] = useState<TicketStatus | 'ALL'>('ALL');
  const [issued, setIssued] = useState<TicketView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Rows highlight briefly when they change, so two managers do not chase the
  // same customer.
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const seen = useRef(new Map<string, string>());

  const load = useCallback(async () => {
    try {
      const res = await api<{
        tickets: TicketView[];
        takings: TakingsView;
        counts: TicketCounts;
      }>(`/api/staff/tickets?status=${filter}`, { token: staff.token });
      const fresh = new Set<string>();
      for (const t of res.tickets) {
        const signature = `${t.status}:${t.refundedPence ?? 0}`;
        const before = seen.current.get(t.id);
        if (before && before !== signature) fresh.add(t.id);
        seen.current.set(t.id, signature);
      }
      if (fresh.size > 0) {
        setChanged(fresh);
        setTimeout(() => setChanged(new Set()), 2500);
      }
      setTickets(res.tickets);
      setTakings(res.takings);
      setCounts(res.counts);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the door list.');
    }
  }, [staff.token, filter]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 5000);
    return () => clearInterval(id);
  }, [load]);

  // The practice quiz has no door at all: no code, no cash, no card.
  /**
   * Searches every event, not just this one, because a refund is asked for
   * after the night by somebody who only knows their name or their code.
   * Debounced so a thumb typing "dave" is four keystrokes, not four queries.
   */
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setHits(null);
      return;
    }
    const t = window.setTimeout(() => {
      api<{ hits: TicketSearchHit[] }>(
        `/api/staff/tickets/search?q=${encodeURIComponent(term)}`,
        { token: staff.token },
      )
        .then((r) => setHits(r.hits))
        .catch(() => setHits([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [query, staff.token]);

  if (event.isPractice) {
    return (
      <Card className="text-center">
        <p className="text-xl font-semibold">Practice quiz</p>
        <p className="mt-3 text-muted">
          Anyone with the link can join, with no code and no charge. There is
          nothing to take on the door.
        </p>
      </Card>
    );
  }

  // On a shared-code night the door has nothing to do but read out the code.
  if (event.isFree) {
    return (
      <Card className="text-center">
        <p className="text-xs uppercase tracking-wide text-muted">Free tonight. The join code is</p>
        <p className="my-6 font-mono text-6xl tracking-[0.2em] text-go">{event.joinCode}</p>
        <p className="text-sm text-muted">
          Same code for everyone. No tickets, no cash, no card &mdash; and exactly the same code
          path as a paid night.
        </p>
      </Card>
    );
  }

  async function issue(kind: 'cash' | 'comp') {
    setBusy(true);
    setError(null);
    try {
      const ticket = await api<TicketView>(`/api/staff/tickets/${kind}`, {
        method: 'POST',
        token: staff.token,
        body: kind === 'cash' ? { amountPence: event.entryFeePence } : { reason: 'Comp' },
      });
      setIssued(ticket);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not issue that ticket.');
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ tickets: TicketView[] }>('/api/staff/tickets/batch', {
        method: 'POST',
        token: staff.token,
        body: { count: batch },
      });
      setJustGenerated(res.tickets);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not generate codes.');
    } finally {
      setBusy(false);
    }
  }

  async function sell(ticket: TicketView, amountPence: number) {
    setBusy(true);
    setError(null);
    try {
      const sold = await api<TicketView>(`/api/staff/tickets/${ticket.id}/sell`, {
        method: 'POST',
        token: staff.token,
        body: { amountPence },
      });
      setIssued(sold);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record that sale.');
    } finally {
      setBusy(false);
    }
  }

  async function refund(ticket: TicketView) {
    setBusy(true);
    try {
      await api('/api/staff/refund', {
        method: 'POST',
        token: staff.token,
        body: { ticketId: ticket.id },
      });
      setRefunding(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The refund did not go through.');
    } finally {
      setBusy(false);
    }
  }

  if (refunding) {
    return (
      <ConfirmRefund
        ticket={refunding}
        busy={busy}
        error={error}
        onCancel={() => setRefunding(null)}
        onConfirm={() => void refund(refunding)}
      />
    );
  }


  return (
    <div className="space-y-4">
      {justGenerated && (
        <Card className="bg-sky/10">
          <div className="flex items-baseline justify-between">
            <p className="text-xs uppercase tracking-wide text-muted">
              {justGenerated.length} codes generated &mdash; unsold
            </p>
            <button className="text-sm text-muted underline" onClick={() => setJustGenerated(null)}>
              Done
            </button>
          </div>
          {/* Selectable, so they can be copied straight into whatever prints
              the tickets. Deliberately not a download: the sandboxed viewer
              cannot save files, and a manager can select text. */}
          <p className="mt-3 select-all break-words font-mono text-lg leading-relaxed">
            {justGenerated.map((t) => t.code).join('  ')}
          </p>
          <p className="mt-3 text-xs text-muted">
            None of these work yet. Tap &ldquo;Sold&rdquo; on a code when you take
            the money for it.
          </p>
        </Card>
      )}

      {issued && (
        <Card className="bg-go/15 text-center">
          <p className="text-xs uppercase tracking-wide text-muted">Ticket issued. Their code is</p>
          <p className="my-3 font-mono text-5xl tracking-[0.2em] text-go">{issued.code}</p>
          <button className="text-sm text-muted underline" onClick={() => setIssued(null)}>
            Done
          </button>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button variant="go" disabled={busy} onClick={() => void issue('cash')}>
          Cash {formatPence(event.entryFeePence)}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => void issue('comp')}>
          Comp
        </Button>
      </div>

      {counts && (
        <Card>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Count label="Unsold" value={counts.unsold} tone="text-chalk" />
            <Count label="Sold, unused" value={counts.sold} tone="text-flag" />
            <Count label="Used" value={counts.used} tone="text-go" />
          </div>
          {(counts.awaitingPayment > 0 || counts.refunded > 0) && (
            <p className="mt-3 text-center text-xs text-muted">
              {counts.awaitingPayment > 0 && `${counts.awaitingPayment} card payment(s) in progress`}
              {counts.awaitingPayment > 0 && counts.refunded > 0 && ' · '}
              {counts.refunded > 0 && `${counts.refunded} refunded`}
            </p>
          )}

          <div className="mt-4 flex items-center gap-2 border-t border-ink-3 pt-4">
            <label className="flex items-center gap-2 text-sm text-muted">
              Print
              <input
                type="number"
                min={1}
                max={100}
                value={batch}
                onChange={(e) =>
                  setBatch(Math.min(100, Math.max(1, Math.round(Number(e.target.value)) || 1)))
                }
                className="w-16 rounded-lg bg-ink px-2 py-1.5 text-center tabular-nums outline-none"
              />
              more
            </label>
            <button
              disabled={busy}
              onClick={() => void generate()}
              className="ml-auto min-h-11 rounded-lg bg-ink-3 px-4 text-sm font-semibold disabled:opacity-40"
            >
              Generate codes
            </button>
          </div>
        </Card>
      )}

      {takings && (
        <Card>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Tally label="Card" pence={takings.cardPence} />
            <Tally label="Cash" pence={takings.cashPence} />
            <Tally label="Net" pence={takings.netPence} signed />
          </div>
          <p className="mt-3 text-center text-xs text-muted">
            {takings.ticketsPaid} sold &middot; {takings.ticketsRedeemed} in the room
            {takings.refundedPence > 0 && ` · ${formatPence(takings.refundedPence)} refunded`}
            {takings.compCount > 0 && ` · ${takings.compCount} comped`}
          </p>
        </Card>
      )}

      {error && <Banner kind="error">{error}</Banner>}

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Find a ticket — name or code, any night"
        className="w-full rounded-xl bg-ink-2 px-4 py-3 outline-none ring-1 ring-ink-3 focus:ring-sky"
      />

      {hits !== null ? (
        <SearchResults
          hits={hits}
          thisEventId={event.id}
          onRefund={(t) => setRefunding(t)}
          onClear={() => setQuery('')}
        />
      ) : null}

      <div className={`flex gap-2 overflow-x-auto pb-1 ${hits !== null ? 'hidden' : ''}`}>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
              filter === f.value ? 'bg-sky text-ink font-semibold' : 'bg-ink-2 text-muted'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <ul className={`space-y-2 ${hits !== null ? 'hidden' : ''}`}>
        {tickets.map((t) => (
          <li
            key={t.id}
            className={`rounded-xl bg-ink-2 p-3 transition-colors duration-500 ${
              changed.has(t.id) ? 'bg-flag/25' : ''
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="font-mono text-lg">{t.code}</span>
              <span className="flex-1 text-sm text-muted">
                {t.source.toLowerCase()} &middot; {formatPence(t.amountPence)}
                {t.issuedByName && ` · ${t.issuedByName}`}
              </span>
              <StatusPill status={t.status} />
            </div>
            {t.status === 'PENDING' && !t.hostedCheckoutUrl && (
              <div className="mt-2 flex gap-3 text-sm">
                <button
                  disabled={busy}
                  onClick={() => void sell(t, event.entryFeePence)}
                  className="text-go underline disabled:opacity-40"
                >
                  Sold &mdash; {formatPence(event.entryFeePence)}
                </button>
                <button
                  disabled={busy}
                  onClick={() => void sell(t, 0)}
                  className="text-muted underline disabled:opacity-40"
                >
                  Give away
                </button>
              </div>
            )}
            {(t.status === 'PAID' || t.status === 'REDEEMED') && t.amountPence > 0 && (
              <button
                disabled={busy}
                onClick={() => setRefunding(t)}
                className="mt-2 text-sm text-stop underline disabled:opacity-40"
              >
                Refund {formatPence(t.amountPence - (t.refundedPence ?? 0))}
              </button>
            )}
            {t.refundedPence ? (
              <p className="mt-1 text-xs text-muted">
                {formatPence(t.refundedPence)} refunded
              </p>
            ) : null}
          </li>
        ))}
        {tickets.length === 0 && (
          <li className="py-8 text-center text-muted">No tickets yet.</li>
        )}
      </ul>
    </div>
  );
}

function Tally({
  label,
  pence,
  signed = false,
}: {
  label: string;
  pence: number;
  signed?: boolean;
}) {
  return (
    <div>
      <p
        className={`text-2xl font-bold tabular-nums ${signed && pence < 0 ? 'text-stop' : ''}`}
      >
        {signed ? formatPenceSigned(pence) : formatPence(pence)}
      </p>
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
    </div>
  );
}

function Count({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div>
      <p className={`text-3xl font-bold tabular-nums ${tone}`}>{value}</p>
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
    </div>
  );
}

function StatusPill({ status }: { status: TicketStatus }) {
  const { tone, label } = {
    PENDING: { tone: 'bg-ink-3 text-muted', label: 'unsold' },
    PAID: { tone: 'bg-flag/20 text-flag', label: 'unused' },
    REDEEMED: { tone: 'bg-go/20 text-go', label: 'used' },
    REFUNDED: { tone: 'bg-stop/20 text-stop', label: 'refunded' },
  }[status];
  return <span className={`rounded-full px-2 py-0.5 text-xs ${tone}`}>{label}</span>;
}

/**
 * Refunding really moves money, at SumUp, immediately -- and the link that
 * starts it sits in a scrolling list somebody is thumbing through with a queue
 * at the door. It takes over the screen the way removing a player does, rather
 * than being an overlay, because the point is to interrupt.
 */
function ConfirmRefund({
  ticket,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  ticket: TicketView;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const amount = ticket.amountPence - (ticket.refundedPence ?? 0);

  return (
    <div className="space-y-4">
      <Card>
        <p className="text-xs uppercase tracking-wide text-muted">Refund this ticket</p>
        <p className="mt-1 font-mono text-2xl font-bold">{ticket.code}</p>
        <p className="mt-2 text-sm text-muted">
          {ticket.source?.toLowerCase()} &middot; {formatPence(ticket.amountPence)} paid
          {ticket.refundedPence ? ` · ${formatPence(ticket.refundedPence)} already refunded` : ''}
        </p>

        <p className="mt-4 text-center text-4xl font-bold text-stop">{formatPence(amount)}</p>

        <p className="mt-4 rounded-lg bg-flag/15 p-3 text-xs text-flag">
          {ticket.source === 'CARD'
            ? 'This refunds the card at SumUp straight away. It cannot be undone from here.'
            : 'This records a cash refund. Hand the money back from the till yourself — the app cannot do that part.'}
          {' '}
          The player is not removed from the quiz; do that on the Players tab if you need to.
        </p>

        {error && (
          <div className="mt-4">
            <Banner kind="error">{error}</Banner>
          </div>
        )}

        <div className="mt-4 flex gap-3">
          <Button variant="danger" disabled={busy} onClick={onConfirm} className="flex-1">
            {busy ? 'Refunding…' : `Refund ${formatPence(amount)}`}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Cross-event hits. A ticket from THIS event can be refunded here; one from
 * another night is shown but not actionable, because a manager PIN is scoped to
 * its own event and refunding another night's money needs the admin token. It
 * says so rather than hiding the row -- "I can see you paid, but I cannot give
 * it back from this screen" is a useful thing to be able to tell somebody.
 */
function SearchResults({
  hits,
  thisEventId,
  onRefund,
  onClear,
}: {
  hits: TicketSearchHit[];
  thisEventId: string;
  onRefund: (t: TicketView) => void;
  onClear: () => void;
}) {
  if (hits.length === 0) {
    return (
      <Card>
        <p className="text-muted">Nothing matching that name or code.</p>
        <button onClick={onClear} className="mt-2 text-sm text-muted underline">
          Clear
        </button>
      </Card>
    );
  }

  return (
    <ul className="space-y-2">
      {hits.map((h) => {
        const t = h.ticket;
        const outstanding = t.amountPence - (t.refundedPence ?? 0);
        const mine = h.eventId === thisEventId;
        return (
          <li key={t.id} className="rounded-xl bg-ink-2 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-lg">{t.code}</span>
              <span className="text-sm text-muted">
                {t.source.toLowerCase()} · {formatPence(t.amountPence)}
              </span>
            </div>
            <p className="mt-1 text-sm">
              {h.playerName ?? <span className="text-muted">not used</span>}
              <span className="text-muted">
                {' · '}
                {h.eventName}
                {h.eventScheduledFor
                  ? ` · ${new Date(h.eventScheduledFor).toLocaleDateString()}`
                  : ''}
                {mine ? ' · tonight' : ''}
              </span>
            </p>
            {t.refundedPence ? (
              <p className="mt-1 text-xs text-muted">
                {formatPence(t.refundedPence)} already refunded
              </p>
            ) : null}
            {outstanding > 0 &&
              (mine ? (
                <button
                  onClick={() => onRefund(t)}
                  className="mt-2 text-sm text-stop underline"
                >
                  Refund {formatPence(outstanding)}
                </button>
              ) : (
                <p className="mt-2 text-xs text-muted">
                  Another night — refund this one from /admin.
                </p>
              ))}
          </li>
        );
      })}
      <li>
        <button onClick={onClear} className="text-sm text-muted underline">
          Clear search
        </button>
      </li>
    </ul>
  );
}
